/**
 * Ant Design Theme Configuration
 * Design system based on UI/UX Pro Max recommendations for SaaS Dashboard
 * Uses shared design tokens from @ai-content-hub/shared-styles
 *
 * EAA-1663: The palette itself (Colors interface, lightColors, darkColors,
 * getColors) moved to @ai-content-hub/shared-styles/src/colors.ts, which is
 * now the single source of truth — this file only re-exports the palette
 * and keeps the white-label override layering (unchanged, still app-local).
 *
 * EAA-1804: The Ant Design theme config generator itself moved to the shared
 * `createAntdTheme` preset in `@ai-content-hub/ui/theme` (shadcn/ui-flavoured
 * antd chrome, shared across every app). This file's `getThemeConfig` is now
 * a thin delegation to that preset, kept here only so the existing public
 * exports (`getThemeConfig`, `themeConfig`, `applyOverridesToColors`, the
 * `Colors`/`getColors` re-exports) — and the `CoreThemeProvider` seam that
 * takes `getThemeConfig` as an injected prop — keep working unchanged.
 */
import type { ThemeConfig } from 'antd';
import {
    lightenHex,
    getColors,
    type Colors,
    type CustomThemeColors,
} from '@ai-content-hub/shared-styles';
import { createAntdTheme } from '@ai-content-hub/ui/theme';

export type { Colors };
export { getColors };

/**
 * Ant Design Theme Config Generator — delegates to the shared preset.
 * No app-specific override is needed today: every token web-content used to
 * set (Table, Button, Card, Input, Select, Modal, Menu, Layout, Segmented)
 * is derived purely from the shared `Colors` object, so it lives entirely in
 * `createAntdTheme` now. If web-content ever needs a token tied to an
 * app-only color key, merge it in here as a shallow override on top.
 */
export const getThemeConfig = (colors: Colors): ThemeConfig => createAntdTheme(colors);

/**
 * Apply custom theme overrides to a Colors object.
 * Maps CustomThemeColors keys to the Colors interface, computing derived values.
 */
export const applyOverridesToColors = (
    base: Colors,
    overrides: Partial<CustomThemeColors>
): Colors => {
    const merged = { ...base };

    if (overrides.primary) {
        merged.primary = overrides.primary;
        merged.secondary = lightenHex(overrides.primary, 20);
    }
    if (overrides.cta) {
        merged.cta = overrides.cta;
        merged.ctaLight = lightenHex(overrides.cta, 90);
    }
    if (overrides.background) {
        merged.background = overrides.background;
        merged.surfaceHover = lightenHex(overrides.background, 3);
    }
    if (overrides.surface) {
        merged.surface = overrides.surface;
        if (!overrides.surfaceElevated) {
            merged.surfaceElevated = overrides.surface;
        }
    }
    if (overrides.surfaceElevated) {
        merged.surfaceElevated = overrides.surfaceElevated;
    }
    if (overrides.text) {
        merged.text = overrides.text;
    }
    if (overrides.textMuted) {
        merged.textMuted = overrides.textMuted;
    }
    if (overrides.border) {
        merged.border = overrides.border;
    }
    if (overrides.sidebarSelectedBg) {
        merged.sidebarMenuSelectedBg = overrides.sidebarSelectedBg;
        merged.accentLight = overrides.sidebarSelectedBg;
    }
    if (overrides.sidebarSelectedText) {
        merged.sidebarMenuSelectedText = overrides.sidebarSelectedText;
    }
    if (overrides.textLight) {
        merged.textLight = overrides.textLight;
    }
    if (overrides.textOnCta) {
        merged.textOnCta = overrides.textOnCta;
    }
    if (overrides.success) {
        merged.success = overrides.success;
    }
    if (overrides.warning) {
        merged.warning = overrides.warning;
    }
    if (overrides.error) {
        merged.error = overrides.error;
    }
    if (overrides.info) {
        merged.info = overrides.info;
    }

    return merged;
};

/**
 * Default export for backwards compatibility
 * @deprecated Use getThemeConfig(colors) instead
 */
export const themeConfig: ThemeConfig = getThemeConfig(getColors(false));

export default themeConfig;
